
import React from "react";
import { ITournamentEditionMatchResult, ITournamentEditionMatchResultProof, TournamentEditionScoringAppliesTo, TournamentEditionScoringType } from "../../../../../services";
import { i18nManager } from "../../../../../i18n";
import { Utils } from "../../../../../utils";
import ResultProof from "./ResultProof";
import { Scheme, ButtonManageRef, AlertInterface, FileInputPreviewType, BoolHelper, DOMHelper } from "@rivtn/noseur";
import { ConfirmDialog } from "../../../../shared/ConfirmDialog";

export function getProofFileType(type: string) {
    if (type.includes("video")) return FileInputPreviewType.VIDEO;
    if (type.includes("image")) return FileInputPreviewType.IMAGE;
    if (type.includes("audio")) return FileInputPreviewType.AUDIO;
    return FileInputPreviewType.CUSTOM;
}

export function previewProof(proof: ITournamentEditionMatchResultProof, onRemove?: (p: ITournamentEditionMatchResultProof) => void, onNavigate?: (p: ITournamentEditionMatchResultProof, direction: "next" | "prev") => void) {
    if (!BoolHelper.equalsAny(proof.type, [FileInputPreviewType.IMAGE, FileInputPreviewType.AUDIO, FileInputPreviewType.VIDEO])) {
        DOMHelper.openInNewTab(proof.location_url);
        return null;
    }
    let dialog: AlertInterface = ConfirmDialog({
        closable: true,
        noControl: true,
        style: { maxWidth: "90%" },
        confirmScheme: Scheme.DANGER,
        content: (<div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <span>{proof.name}</span>
                    <span style={{ opacity: 0.7 }}>{proof.size}</span>
                </div>
                <div style={{ display: "flex", }}>
                    <i style={{ cursor: "pointer", fontSize: 20, color: "#FF7171" }} className="fa fa-trash" onClick={() => {
                        dialog?.hide(() => onRemove?.(proof));
                    }} />
                    <i style={{ cursor: "pointer", fontSize: 20, marginLeft: 15 }} className="fa fa-times" onClick={() => dialog?.hide()} />
                </div>
            </div>
            <div className="ma-proof-prev">
                <i style={{ cursor: "pointer" }} className="fa fa-angle-left" onClick={() => onNavigate?.(proof, "prev")} />
                <div className="view">
                    {(proof.type === FileInputPreviewType.AUDIO) && (<audio src={proof.location_url} controls />)}
                    {(proof.type === FileInputPreviewType.VIDEO) && (<video src={proof.location_url} controls />)}
                    {(proof.type === FileInputPreviewType.IMAGE) && (<img alt={proof.name} src={proof.location_url} />)}
                </div>
                <i style={{ cursor: "pointer" }} className="fa fa-angle-right" onClick={() => onNavigate?.(proof, "next")} />
            </div>
        </div>),
        onConfirm: (button?: ButtonManageRef) => {
            button?.setLoadingState(true);
            return false;
        }
    });
    dialog.show();
    return dialog;
}

export function removeProof(proof: ITournamentEditionMatchResultProof) {
    const dialogLabels = i18nManager.Labels.tournaments.your_tournament.matches_and_reporting.match.dialogs.delete_proof;

    const dialog = ConfirmDialog({
        closable: true,
        style: { maxWidth: 500 },
        title: dialogLabels.title,
        confirmScheme: Scheme.DANGER,
        desc: dialogLabels.desc.replaceAll("${proof_name}", proof.name),
        onConfirm: (button?: ButtonManageRef) => {
            button?.setLoadingState(true);
            return false;
        }
    });
    dialog.show();
}

export interface ResultProps {
    pid?: string;
    isAccepted?: boolean;
    isManaging?: boolean;
    acceptedProofs?: string[];
    style?: React.CSSProperties;
    result: ITournamentEditionMatchResult;
    onAccept?: (p: ITournamentEditionMatchResultProof) => void;
    onAcceptRemove?: (p: ITournamentEditionMatchResultProof) => void;
}

function Result(props: ResultProps) {

    const labels = i18nManager.Labels.tournaments.your_tournament.matches_and_reporting.match.result;
    const { result, pid, style, isManaging, acceptedProofs, onAccept, isAccepted, onAcceptRemove } = props;
    const name = (isAccepted ? "" : (result.tournament_scoring?.type === TournamentEditionScoringType.TOURNAMENT_SCORE ? "# " + i18nManager.Labels.host.manage_tournament.scoring.detail.tournament_score : result.tournament_scoring?.name ?? "#"));

    return (<div className="ma-result" style={style}>
        {(!isAccepted) && (<>
            {(result.tournament_scoring?.applies_to === TournamentEditionScoringAppliesTo.PARTICIPANT) && (<span className="t" style={{ fontWeight: "bold" }}>
                {name} - <span style={{ fontSize: 13, opacity: 0.7, fontWeight: "normal" }}>{Utils.formatToFineDate(result.date ?? new Date())}</span>
            </span>)}
            {(!isManaging) && (<span style={{ fontSize: 14, marginTop: 4 }}>{labels.submitted_by} <b>{Utils.getResourceName(result.tournament_edition_registered_participants?.find((p) => p.external_id === result.submitter_id)?.resource, result.submitter_id)}</b></span>)}
            {/* {(result.type === TournamentMatchResultType.PARTICIPANT) && (<>
            <span className="t" dangerouslySetInnerHTML={{ __html: labels.your_submitted_proof.replaceAll("${score}", score) }} />
            <span className="n">{labels.your_submitted_proof_note}</span>
        </>)} */}
            {(!isManaging) && (<span className="n">{labels.your_submitted_proof_note}</span>)}
            {Object.entries(result.scores).map(([id, scores]) => <span key={id} className="d">
                {Utils.getResourceName(result.tournament_edition_registered_participants?.find((p) => p.external_id === id)?.resource, id)} {pid === id ? `(${labels.you})` : ""}: {Object.entries(scores).map(([sid, v], i) => (<React.Fragment key={i}>{(i !== 0) && (", ")}<b>{result.tournament_scoring?.scoring?.[sid]?.submission_name ?? sid}</b> - {v}</React.Fragment>))}
            </span>)}
            {/* {(result.type === TournamentMatchResultType.TEAM_MEMBER) && (<>
            <div className="u" style={{ margin: "10px 0px" }}>
                <img alt={result.team_member_score?.member.username} src={result.team_member_score?.member.profile_picture_url} />
                <div className="d">
                    <span className="t">{result.team_member_score?.member?.username}</span>
                    <span className="lined-limit">{result.team_member_score?.member?.tag_line}</span>
                </div>
            </div>
            <span className="c">{result.team_member_score?.score_or_comment}</span>
        </>)} */}
        </>)}
        <div className="ma-proofs">
            {result.proofs?.map((proof) => (<ResultProof isManaging={isManaging && !isAccepted} isAcceptedPreview={isManaging && isAccepted} isAccepted={acceptedProofs?.includes(proof.location_url)} key={proof.location_url} proof={proof}
                remove={() => (isAccepted ? onAcceptRemove : removeProof)?.(proof)}
                preview={() => {
                    let dialog: AlertInterface | null;
                    const onNavigate = (proof: ITournamentEditionMatchResultProof, direction: "next" | "prev") => {
                        const i = result.proofs.findIndex((p) => p.location_url === proof.location_url);
                        if (direction === "prev" && (i > 0)) {
                            dialog?.destroy(() => previewProof(result.proofs[i - 1], (isAccepted ? onAcceptRemove : removeProof), onNavigate));
                        } else if (direction === "next" && (i < (result.proofs.length - 1))) {
                            dialog?.destroy(() => previewProof(result.proofs[i + 1], (isAccepted ? onAcceptRemove : removeProof), onNavigate));
                        }
                    };
                    dialog = previewProof(proof, (isAccepted ? onAcceptRemove : removeProof), onNavigate);
                }} accept={() => {
                    onAccept?.(proof);
                }} />))}
        </div>
    </div>);
}

export default Result;
